import React from "react";
import { RentalDateField } from "@glamcorner/components/lib/core/components/RentalDatePickerField";
import { OnDemandEvents } from "@glamcorner/gc-types/generated/ipcBusEvents";
import { DesignationPanel, DesignationPanelField } from "../DesignationPanel";
import { Postcode } from "../../../../components/Postcode";
import { SizeRow } from "../../SizeRow";
import { RentalPeriods } from "../../RentalPeriods";
import { Bus } from "../../../../services/Bus";
import { RentalDesignationInformation } from "../../../../types";
import { useRental } from "../../../context/RentalProvider";
import { numberInCentsToDisplay } from "../../../pricing";
import "./rental-designation-panel.scss";

export type RentalDesignationPanelProps = {
  isSelected: boolean;
  onSelect: () => void;
  parentOnSubmitCallback: (callbackFn: () => boolean) => void;
  productCode: string | undefined;
  information: RentalDesignationInformation;
  errors: { size?: string };
  isValid: () => boolean;
  size: string | undefined;
};

export const RentalDesignationPanel: React.FC<RentalDesignationPanelProps> = ({
  isSelected,
  onSelect,
  parentOnSubmitCallback,
  productCode,
  information,
  size,
}) => {
  const {
    availableDeliveryDates,
    setRentalPeriod,
    validateRental,
    rentalPeriod,
    errors,
    postcode,
    setPostcode,
    setRentalDates,
    rentalDates,
  } = useRental();

  const getSubTitle = () => {
    // Find the lowest rental periods
    const priceForPeriod = information.prices
      .map((price) => price.discountPriceInCents || price.priceInCents)
      .sort((a, b) => a - b)[0]; // By default sort() sorts as STRINGS! >:C

    if (priceForPeriod) {
      const price = numberInCentsToDisplay(priceForPeriod);
      return `from $${price}`;
    }

    return "";
  };

  const sizeRowField: DesignationPanelField = {
    label: "Size",
    error: errors.size,
    component: <SizeRow sizes={information.sizes} />,
  };

  const postcodeField: DesignationPanelField = {
    label: "Postcode",
    error: undefined, // Handled by the component
    component: (
      <Postcode
        isRequiredField
        onChange={setPostcode}
        onEnterKey={setPostcode}
        value={postcode}
        errorMessage={errors.postcode}
        withMessageSpacing={true}
      />
    ),
  };

  const rentalDays = information.prices.map((price) => price.rentalDays);

  const rentalPeriodField: DesignationPanelField = {
    label: "Rental Period",
    component: <RentalPeriods selectedDays={rentalPeriod} onSelect={setRentalPeriod} rentalDays={rentalDays} />,
    error: undefined,
  };

  const datepickerEnabled = Boolean(postcode && productCode);

  // This wrapper is to fix bug with SSR and hydration not applying classes
  // https://github.com/GlamCorner/catalogue/issues/143
  // https://github.com/facebook/react/issues/13260
  const OpacityWrapper: React.FC<{ active: boolean }> = ({ active, children }) => {
    return active ? <div style={{ opacity: 0.3 }}>{children}</div> : <>{children}</>;
  };

  const deliveryDateField: DesignationPanelField = {
    label: "Delivery Date",
    wrapperClass: "c-designation-panel__delivery-date--wrapper",
    error: undefined, // Handled by the component
    component: (
      <div onClick={() => !datepickerEnabled && validateRental()}>
        <OpacityWrapper active={!datepickerEnabled}>
          <RentalDateField
            initialAppliedDates={rentalDates.startDate === "" ? undefined : rentalDates}
            isDressAvailable={() => {
              // If endpoint returns an empty array, fail-safe and mark them all as unavailable
              return availableDeliveryDates.length !== 0;
            }}
            availableDeliveryDates={availableDeliveryDates}
            onDatePicked={setRentalDates}
            rentalPeriod={rentalPeriod}
            shouldShowDatePicker={datepickerEnabled}
            errorMessage={datepickerEnabled ? errors.rentalDates : undefined}
          />
        </OpacityWrapper>
      </div>
    ),
  };

  parentOnSubmitCallback(() => {
    if (validateRental()) {
      const pathname = window.location.pathname;
      // Originally magento would check for 'checkout-cart-configure' class on body
      // We will check the URL instead because the next parameter is the magento quote ID of the item
      // Makes it more of a single edit dependency
      const isEditing = pathname.includes("/checkout/cart/configure");

      if (isEditing) {
        const pathArray = pathname.split("/");
        const lastItemID = pathArray[pathArray.length - 1];

        Bus.broadcast(OnDemandEvents.CartUpdateItem, {
          itemId: lastItemID,
          productCode,
          productType: "Rental",
          postcode,
          startDate: rentalDates.startDate,
          endDate: rentalDates.endDate,
          size,
        });
        return true;
      } else {
        Bus.broadcast(OnDemandEvents.AddToCart, {
          productCode,
          productType: "Rental",
          postcode,
          startDate: rentalDates.startDate,
          endDate: rentalDates.endDate,
          inBackupMode: GC.backupDress.isEnabled(),
          size,
        });
        return true;
      }
    }

    return false;
  });

  return (
    <DesignationPanel
      isSelected={isSelected}
      title="One Time Rental"
      subTitle={getSubTitle()}
      onSelect={onSelect}
      fields={[[sizeRowField], [rentalPeriodField], [postcodeField, deliveryDateField]]}
    />
  );
};
